export type AccessorAttributes = {
  threshold?: Ranking;
  maxRanking: Ranking;
  minRanking: Ranking;
};

export interface RankingInfo {
  rankedValue: any;
  rank: Ranking;
  accessorIndex: number;
  accessorThreshold: Ranking | undefined;
  passed: boolean;
}

export interface AccessorOptions<TItem> {
  accessor: AccessorFn<TItem>;
  threshold?: Ranking;
  maxRanking?: Ranking;
  minRanking?: Ranking;
}

export type AccessorFn<TItem> = (item: TItem) => string | Array<string>;

export type Accessor<TItem> = AccessorFn<TItem> | AccessorOptions<TItem>;

export interface RankItemOptions<TItem = unknown> {
  accessors?: ReadonlyArray<Accessor<TItem>>;
  threshold?: Ranking;
  keepDiacritics?: boolean;
}

export const rankings = {
  CASE_SENSITIVE_EQUAL: 7,
  EQUAL: 6,
  STARTS_WITH: 5,
  WORD_STARTS_WITH: 4,
  CONTAINS: 3,
  ACRONYM: 2,
  MATCHES: 1,
  NO_MATCH: 0,
} as const;

export type Ranking = (typeof rankings)[keyof typeof rankings];

export function rankItem<TItem>(item: TItem, value: string): RankingInfo {
  const rank = getMatchRanking(item as unknown as string, value);

  return {
    rankedValue: item,
    rank,
    accessorIndex: -1,
    accessorThreshold: rankings.MATCHES,
    passed: rank >= rankings.MATCHES,
  };
}

function getMatchRanking(testString: any, stringToRank: string): Ranking {
  testString = `${testString}`;
  stringToRank = `${stringToRank}`;

  if (stringToRank.length > testString.length) {
    return rankings.NO_MATCH;
  }

  if (testString === stringToRank) {
    return rankings.CASE_SENSITIVE_EQUAL;
  }

  testString = testString.toLowerCase();
  stringToRank = stringToRank.toLowerCase();

  if (testString === stringToRank) {
    return rankings.EQUAL;
  }

  if (testString.startsWith(stringToRank)) {
    return rankings.STARTS_WITH;
  }

  if (testString.includes(` ${stringToRank}`)) {
    return rankings.WORD_STARTS_WITH;
  }

  if (testString.includes(stringToRank)) {
    return rankings.CONTAINS;
  } else if (stringToRank.length === 1) {
    return rankings.NO_MATCH;
  }

  if (getAcronym(testString).includes(stringToRank)) {
    return rankings.ACRONYM;
  }

  return rankings.NO_MATCH;
}

function getAcronym(string: string): string {
  let acronym = "";
  const wordsInString = string.split(" ");
  wordsInString.forEach((wordInString) => {
    const splitByHyphenWords = wordInString.split("-");
    splitByHyphenWords.forEach((splitByHyphenWord) => {
      acronym += splitByHyphenWord.substr(0, 1);
    });
  });
  return acronym;
}
